<script setup lang="ts">
import { Banner } from '@/components';
import { CompleteYourProfile } from '@/components/account';
import SimpleAddressForm from '@/components/address/SimpleAddressForm.vue';
import JobForm from '@/components/support-seeker/forms/JobForm.vue';
import { JobList } from '@/components/support-seeker/lists';
import { SupportWorkerList } from '@/components/support-worker/lists';
import { loginCheck } from '@/core/services/LoginCheck';
import { useAuthStore } from '@/modules/auth/store/auth';
import { capitalize } from '@/utils/Helpers';
import { isEmpty } from 'lodash';
import { storeToRefs } from 'pinia';

useHead({
  title: 'Home',
});

definePageMeta({
  auth: true,
  alias: '/home',
});

const { getSession } = useUserAuth();
await getSession();

const { getUser, hasLoggedInUser, isClient, isWorker } = storeToRefs(useAuthStore());
onMounted(async () => {
  if (!hasLoggedInUser.value || !getUser.value?.email_verified) {
    await navigateTo({ name: 'login' });
  } else {
    const { setOptions } = useAuthStore();
    const { options } = storeToRefs(useAuthStore());
    if (!options.value.skipOnboarding) {
      setOptions({ skipOnboarding: true });
      // Only run loginCheck if user has a user_type (has completed welcome step)
      // This prevents redirect loop when user comes from welcome page
      if (getUser.value?.user_type) {
        await loginCheck(false);
      }
    }
  }
});

const showJobForm = ref(false);
const handleJobForm = (bool: boolean): void => {
  showJobForm.value = bool;
};

const welcomeLabel = computed((): string => {
  return `Hi ${capitalize((getUser.value?.first_name || '').toLowerCase())}!`;
});

// Refs for child components to trigger refresh
const supportWorkerListRef = ref(null);
const jobListRef = ref(null);
const recommendedJobListRef = ref(null);

// Reactive variables to store job listings lengths
const recommendedJobListingsLength = ref(0);
const recentJobListingsLength = ref(0);

// Address change handler
const handleAddressChange = async () => {
  console.log('Address changed, refreshing data...');
  
  // Check if we're on the home page
  const route = useRoute();
  if (route.name !== 'index' && route.name !== 'home') {
    return;
  }
  
  // Add a small delay to ensure the address change has been processed
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Refresh API calls based on user type
  try {
    if (isClient.value && supportWorkerListRef.value) {
      console.log('Refreshing support worker list...');
      // Force refresh by calling the component's fetch method
      if (typeof supportWorkerListRef.value.refreshData === 'function') {
        await supportWorkerListRef.value.refreshData();
      } else if (typeof supportWorkerListRef.value.fetch === 'function') {
        await supportWorkerListRef.value.fetch(1);
      }
    }
    
    if (isWorker.value) {
      if (recommendedJobListRef.value) {
        console.log('Refreshing recommended job list...');
        if (typeof recommendedJobListRef.value.refreshData === 'function') {
          await recommendedJobListRef.value.refreshData();
        } else if (typeof recommendedJobListRef.value.fetch === 'function') {
          await recommendedJobListRef.value.fetch(1);
        }
      }
      if (jobListRef.value) {
        console.log('Refreshing recent job list...');
        if (typeof jobListRef.value.refreshData === 'function') {
          await jobListRef.value.refreshData();
        } else if (typeof jobListRef.value.fetch === 'function') {
          await jobListRef.value.fetch(1);
        }
      }
    }
  } catch (error) {
    console.error('Error refreshing data after address change:', error);
  }
};

// Listen for global address change events
onMounted(() => {
  // Listen for address changes via window events
  window.addEventListener('address-changed', handleAddressChange);
});

onBeforeUnmount(() => {
  // Clean up event listeners
  window.removeEventListener('address-changed', handleAddressChange);
});

// Provide the address change handler to child components
provide('onAddressChange', handleAddressChange);
</script>

<template>
  <div>
    <template v-if="!showJobForm">
      <Banner :title="welcomeLabel" @handleJobForm="handleJobForm" class="mb-6" />
      <!-- <CompleteYourProfile class="mb-6" /> -->

      <template v-if="isClient">
        <SupportWorkerList
          ref="supportWorkerListRef"
          :recommended="true"
          title="Support Workers in your Community"
          subtitle="Use search to find the right Support Worker for you"
          @handleJobForm="handleJobForm"
          :isFilter="false"
        />
      </template>
      <!-- :subtitle="recommendedJobListingsLength ? 'jobs listings matched your preference' : 'no job listings matched your preference'" -->
      <template v-if="isWorker">
        <JobList
          ref="recommendedJobListRef"
          :carousel="true"
          :recommended="true"
          title="Job listings"
          parent-class="mb-6"
          @jobListingsLength="(length) => recommendedJobListingsLength = length"
        />
        <!-- <JobList 
          ref="jobListRef"
          :carousel="true" 
          title="Recent job listings" 
          :subtitle="recentJobListingsLength ? 'job listings were recently posted' : 'no job listings were recently posted'"
          @jobListingsLength="(length) => recentJobListingsLength = length"
        /> -->
      </template>
    </template>
    <JobForm v-if="showJobForm" v-model="showJobForm" :job-listing="{}" />
  </div>
</template>

<style lang="scss" scoped>
.container {
  &__banner {
  }
}
</style>
